/**
 * SHARED with the Cloud Functions (backlog #115).
 *
 * Everything in `shared/` is imported by BOTH halves of the app: the web
 * bundle compiles it with `src/`, and `functions/scripts/build.mjs` inlines it
 * into `functions/lib/index.js` with esbuild, because Firebase uploads only
 * the `functions/` directory. There is no copy and no generated file — one
 * source of truth, edited here.
 *
 * Three rules follow from that:
 *
 * - **Stay pure.** No I/O, no Firebase SDK, no browser or node globals. It has
 *   to run in both places.
 * - **Import nothing but siblings in this directory** — no npm packages (the
 *   functions build leaves bare imports as runtime requires) and never back
 *   into `src/`. Both are lint-enforced and pinned by
 *   `functions/src/__tests__/sharedBoundary.test.ts`.
 * - **Type structurally.** No `src/types` imports; declare the minimum shape
 *   the function needs, so a caller on either side satisfies it.
 *
 * **A change here needs a `functions` deploy**, not just hosting — the CI
 * path filter watches `shared/**` for exactly that reason (Lessons #84, #94).
 */
/**
 * Singapore public holidays — used to skip non-working days when computing
 * earliest-available measurement dates and when validating slot bookings.
 *
 * VERIFY ANNUALLY against MOM's gazetted list:
 *   https://www.mom.gov.sg/employment-practices/public-holidays
 *
 * Singapore observes the "in-lieu" rule: when a public holiday falls on a
 * Sunday, the following Monday is observed as a holiday. Where this applies,
 * the in-lieu date is listed as a separate entry below.
 *
 * This file is inlined into the Cloud Functions bundle at build time,
 * so this is the single source — the annual update is a one-file change here.
 */

export interface PublicHoliday {
  /** Calendar date in YYYY-MM-DD (local time, Singapore). */
  date: string;
  /** Human label shown in tooltips. */
  name: string;
  /** True if this entry is an in-lieu holiday for a holiday that fell on a Sunday. */
  inLieu?: boolean;
}

export const SG_PUBLIC_HOLIDAYS: PublicHoliday[] = [
  // ── 2026 ────────────────────────────────────────────────────────────────
  { date: '2026-01-01', name: "New Year's Day" },
  // Lunar — VERIFY: Chinese New Year 2026 is widely listed as 17–18 Feb.
  { date: '2026-02-17', name: 'Chinese New Year (Day 1)' },
  { date: '2026-02-18', name: 'Chinese New Year (Day 2)' },
  // Lunar — VERIFY: Hari Raya Puasa 2026.
  { date: '2026-03-20', name: 'Hari Raya Puasa' },
  // Computed (Christian calendar) — Good Friday 2026 = 3 April.
  { date: '2026-04-03', name: 'Good Friday' },
  { date: '2026-05-01', name: 'Labour Day' },
  // Lunar — VERIFY: Hari Raya Haji 2026.
  { date: '2026-05-27', name: 'Hari Raya Haji' },
  // Buddhist — VERIFY: Vesak Day 2026.
  { date: '2026-05-31', name: 'Vesak Day' },
  { date: '2026-06-01', name: 'Vesak Day (in-lieu)', inLieu: true },
  // National Day 2026 falls on Sunday → Mon 10 Aug is gazetted in-lieu.
  { date: '2026-08-09', name: 'National Day' },
  { date: '2026-08-10', name: 'National Day (in-lieu)', inLieu: true },
  // Hindu — VERIFY: Deepavali 2026.
  { date: '2026-11-08', name: 'Deepavali' },
  { date: '2026-11-09', name: 'Deepavali (in-lieu)', inLieu: true },
  { date: '2026-12-25', name: 'Christmas Day' },

  // ── 2027 ────────────────────────────────────────────────────────────────
  // Add 2027 dates closer to year-end 2026 once MOM publishes the gazette.
  { date: '2027-01-01', name: "New Year's Day" },
  { date: '2027-05-01', name: 'Labour Day' },
  { date: '2027-08-09', name: 'National Day' },
  { date: '2027-12-25', name: 'Christmas Day' },
];

const HOLIDAY_KEYS = new Set(SG_PUBLIC_HOLIDAYS.map((h) => h.date));
const HOLIDAY_INDEX = new Map(SG_PUBLIC_HOLIDAYS.map((h) => [h.date, h]));

/** Format a Date as YYYY-MM-DD using local time. Mirrors scheduling.ts. */
const localDateKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const isPublicHoliday = (date: Date): boolean => HOLIDAY_KEYS.has(localDateKey(date));

export const getHoliday = (date: Date): PublicHoliday | undefined =>
  HOLIDAY_INDEX.get(localDateKey(date));
